import { useCallback, useEffect, useRef, useState } from 'react'
import { createDecartClient, models } from '@decartai/sdk'
import type { RealTimeClient } from '@decartai/sdk'
import {
  MirrorApiError,
  createMirrorSession,
  fetchMirrorStatus,
  fetchPublicConfig,
  type MirrorStatus,
} from '../../lib/api'
import { useLiff } from '../../hooks/useLiff'

const FIXED_PROMPT = 'Substitute the current outfit with the garment shown in the reference image'
const MODEL_NAME = 'lucy-vton-latest' as const
const LOGIN_RETRY_KEY = 'mirror_login_retry'
const MAX_ITEM_EDGE = 1024

type Phase = 'idle' | 'requesting' | 'connecting' | 'live'
type Facing = 'user' | 'environment'

async function resizeImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, MAX_ITEM_EDGE / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas unavailable')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close?.()
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.9)
  )
}

function errorText(err: unknown): string {
  if (err instanceof MirrorApiError) {
    if (err.kind === 'no_credit') return 'ミラーの残り回数がありません'
    if (err.kind === 'unavailable') return '通信エラーが発生しました。時間をおいてお試しください'
    if (err.kind === 'decart_failed') return '接続に失敗しました。時間をおいてお試しください'
    return 'エラーが発生しました。時間をおいてお試しください'
  }
  const text = err instanceof Error ? `${err.message} ${JSON.stringify((err as { data?: unknown }).data ?? '')}` : ''
  if (text.includes('1013')) return '混み合っています。時間をおいてお試しください'
  return '接続に失敗しました。時間をおいてお試しください'
}

const pillClass =
  'flex items-center justify-center gap-2 rounded-full bg-black/60 px-5 h-11 text-sm font-medium text-white backdrop-blur-md disabled:opacity-50'

export default function LiffMirrorPage() {
  const [liffId, setLiffId] = useState('')
  const [richmenuLiffId, setRichmenuLiffId] = useState('')
  const [configError, setConfigError] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')
  const [facing, setFacing] = useState<Facing>('user')
  const [cameraError, setCameraError] = useState('')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<MirrorStatus | null>(null)
  const [itemUrl, setItemUrl] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(60)

  const localVideoRef = useRef<HTMLVideoElement>(null)
  const remoteVideoRef = useRef<HTMLVideoElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const cameraStreamRef = useRef<MediaStream | null>(null)
  const clientRef = useRef<RealTimeClient | null>(null)
  // Decart に渡すカメラ映像の複製。切断時に SDK（LiveKit）が渡したトラックを止めるため、プレビュー用の元トラックは渡さない
  const sessionStreamRef = useRef<MediaStream | null>(null)
  const itemBlobRef = useRef<Blob | null>(null)
  const itemUrlRef = useRef<string | null>(null)
  const startingRef = useRef(false)
  const timerRef = useRef<number | null>(null)
  const sessionRef = useRef(0)
  const modelRef = useRef(models.realtime(MODEL_NAME))

  useEffect(() => {
    let cancelled = false
    fetchPublicConfig()
      .then((c) => {
        if (cancelled) return
        if (c.liffRichmenuAppId) setRichmenuLiffId(c.liffRichmenuAppId)
        if (c.liffMirrorAppId) setLiffId(c.liffMirrorAppId)
        else setConfigError('LIFF IDが設定されていません。')
      })
      .catch(() => !cancelled && setConfigError('設定の取得に失敗しました。'))
    return () => {
      cancelled = true
    }
  }, [])

  const { isLoading: liffLoading, isLoggedIn, error: liffError } = useLiff({ liffId, autoLogin: true })

  const getToken = () => window.liff?.getIDToken() ?? null

  const reloginOnce = useCallback(() => {
    try {
      if (sessionStorage.getItem(LOGIN_RETRY_KEY)) {
        sessionStorage.removeItem(LOGIN_RETRY_KEY)
        setMessage('ログインに失敗しました。LINEから開き直してください')
        return
      }
      sessionStorage.setItem(LOGIN_RETRY_KEY, '1')
    } catch {
      // Ignore
    }
    window.liff?.login({ redirectUri: window.location.href })
  }, [])

  const refreshStatus = useCallback(async () => {
    const token = getToken()
    if (!token) return
    try {
      setStatus(await fetchMirrorStatus(token))
      try {
        sessionStorage.removeItem(LOGIN_RETRY_KEY)
      } catch {
        // Ignore
      }
    } catch (err) {
      if (err instanceof MirrorApiError && err.kind === 'unauthorized') reloginOnce()
      else if (err instanceof MirrorApiError && err.kind === 'unavailable') setMessage(errorText(err))
    }
  }, [reloginOnce])

  useEffect(() => {
    if (isLoggedIn) void refreshStatus()
  }, [isLoggedIn, refreshStatus])

  // Camera
  useEffect(() => {
    let cancelled = false
    let stream: MediaStream | null = null
    setCameraError('')
    const { width, height } = modelRef.current
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: facing, width, height }, audio: false })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        stream = s
        cameraStreamRef.current = s
        if (localVideoRef.current) localVideoRef.current.srcObject = s
      })
      .catch(() => !cancelled && setCameraError('カメラを起動できませんでした。カメラの使用を許可してください'))
    return () => {
      cancelled = true
      stream?.getTracks().forEach((t) => t.stop())
      if (cameraStreamRef.current === stream) cameraStreamRef.current = null
    }
  }, [facing])

  const stopSession = useCallback(() => {
    sessionRef.current++
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    const client = clientRef.current
    clientRef.current = null
    try {
      client?.disconnect()
    } catch {
      // Ignore
    }
    sessionStreamRef.current?.getTracks().forEach((t) => t.stop())
    sessionStreamRef.current = null
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null
    setPhase('idle')
  }, [])

  // Cleanup on unmount
  useEffect(() => () => stopSession(), [stopSession])

  const endWithError = useCallback(
    (text: string) => {
      stopSession()
      setMessage(text)
      void refreshStatus()
    },
    [stopSession, refreshStatus]
  )

  const start = async () => {
    if (startingRef.current) return
    if (phase !== 'idle' || !itemBlobRef.current || !cameraStreamRef.current) return
    startingRef.current = true
    const token = getToken()
    if (!token) {
      startingRef.current = false
      reloginOnce()
      return
    }
    const initialBlob = itemBlobRef.current
    setMessage('')
    setPhase('requesting')
    const id = ++sessionRef.current
    try {
      const session = await createMirrorSession(token)
      if (id !== sessionRef.current) return
      setStatus({ remaining: session.remaining, unlimited: session.unlimited, maxSeconds: session.maxSeconds })
      if (session.remaining === null && !session.unlimited) void refreshStatus()
      setPhase('connecting')
      setSecondsLeft(session.maxSeconds)

      const startedAt = Date.now()
      timerRef.current = window.setInterval(() => {
        const left = Math.max(0, session.maxSeconds - Math.floor((Date.now() - startedAt) / 1000))
        setSecondsLeft(left)
        if (left <= 0) {
          stopSession()
          void refreshStatus()
        }
      }, 250)

      const sessionStream = new MediaStream(cameraStreamRef.current.getVideoTracks().map((t) => t.clone()))
      sessionStreamRef.current = sessionStream
      const client = await createDecartClient({ apiKey: session.token }).realtime.connect(
        sessionStream,
        {
          model: modelRef.current,
          initialState: {
            prompt: { text: FIXED_PROMPT, enhance: true },
            image: initialBlob,
          },
          onRemoteStream: (stream) => {
            if (id !== sessionRef.current) return
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = stream
              void remoteVideoRef.current.play().catch(() => {})
            }
            setPhase('live')
          },
        }
      )
      if (id !== sessionRef.current) {
        client.disconnect()
        return
      }
      clientRef.current = client
      if (itemBlobRef.current !== initialBlob) {
        void client.set({ prompt: FIXED_PROMPT, enhance: true, image: itemBlobRef.current }).catch(() => {})
      }
      client.on('error', (err) => {
        if (id === sessionRef.current) endWithError(errorText(err))
      })
      client.on('sessionEnded', () => {
        if (id === sessionRef.current) {
          stopSession()
          void refreshStatus()
        }
      })
      client.on('connectionChange', (state) => {
        if (id === sessionRef.current && state === 'disconnected') {
          stopSession()
          void refreshStatus()
        }
      })
    } catch (err) {
      if (id !== sessionRef.current) return
      if (err instanceof MirrorApiError && err.kind === 'unauthorized') {
        stopSession()
        reloginOnce()
        return
      }
      endWithError(errorText(err))
    } finally {
      startingRef.current = false
    }
  }

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const blob = await resizeImage(file)
      itemBlobRef.current = blob
      if (itemUrlRef.current) URL.revokeObjectURL(itemUrlRef.current)
      const url = URL.createObjectURL(blob)
      itemUrlRef.current = url
      setItemUrl(url)
      setMessage('')
      const client = clientRef.current
      if (client) {
        await client.set({ prompt: FIXED_PROMPT, enhance: true, image: blob })
      }
    } catch {
      setMessage('画像を読み込めませんでした')
    }
  }

  useEffect(
    () => () => {
      if (itemUrlRef.current) URL.revokeObjectURL(itemUrlRef.current)
      itemUrlRef.current = null
    },
    []
  )

  const flip = () => setFacing((f) => (f === 'user' ? 'environment' : 'user'))
  const mirrored = facing === 'user'
  const inSession = phase === 'connecting' || phase === 'live'
  const noCredit = !!status && !status.unlimited && status.remaining === 0
  const fatal = configError || liffError
  const plansUrl = richmenuLiffId
    ? `https://liff.line.me/${encodeURIComponent(richmenuLiffId)}?target=billing`
    : ''

  const openPicker = () => inputRef.current?.click()

  return (
    <main className="fixed inset-0 overflow-hidden bg-black text-white">
      <video
        ref={localVideoRef}
        autoPlay
        playsInline
        muted
        className="absolute inset-0 h-full w-full object-cover"
        style={{ transform: mirrored ? 'scaleX(-1)' : undefined }}
      />
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        muted
        className="absolute inset-0 h-full w-full object-cover"
        style={{ transform: mirrored ? 'scaleX(-1)' : undefined, display: phase === 'live' ? 'block' : 'none' }}
      />

      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />

      {inSession && (
        <div className="absolute right-4 top-4 rounded-full bg-black/60 px-3 py-1 text-xs backdrop-blur-md">
          {phase === 'connecting' ? '接続中…' : `のこり${secondsLeft}秒`}
        </div>
      )}

      {(fatal || cameraError || liffLoading) && (
        <div className="absolute inset-x-6 top-1/3 rounded-xl bg-black/70 p-4 text-center text-sm">
          {fatal || cameraError || 'LIFFを初期化しています…'}
        </div>
      )}

      <div
        className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 px-4"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        {(message || noCredit) && (
          <div className="rounded-full bg-black/60 px-4 py-2 text-xs backdrop-blur-md" role="status">
            {message || 'ミラーの残り回数がありません'}
          </div>
        )}
        {noCredit && !inSession && plansUrl && (
          <a href={plansUrl} className="rounded-full bg-white px-5 py-2 text-sm font-medium text-black">
            プランを見る
          </a>
        )}
        {status && !status.unlimited && status.remaining !== null && !noCredit && (
          <div className="rounded-full bg-black/60 px-3 py-1 text-xs backdrop-blur-md">のこり{status.remaining}回</div>
        )}

        <div className="relative flex w-full items-center justify-center">
          {itemUrl && (
            <button
              type="button"
              aria-label="アイテムを選び直す"
              onClick={openPicker}
              className="absolute left-0 h-11 w-11 overflow-hidden rounded-lg border border-white/80 bg-black/60"
            >
              <img src={itemUrl} alt="" className="h-full w-full object-cover" />
            </button>
          )}

          {!itemUrl ? (
            <button type="button" className={pillClass} onClick={openPicker}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              アイテムを選ぶ
            </button>
          ) : inSession ? (
            <button type="button" className={pillClass} onClick={() => { stopSession(); void refreshStatus() }}>
              <span className="h-3 w-3 rounded-[2px] bg-red-500" />
              ミラーを止める
            </button>
          ) : (
            <button
              type="button"
              className={pillClass}
              onClick={start}
              disabled={phase === 'requesting' || noCredit || !isLoggedIn || !!cameraError}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 8a2 2 0 0 1 2-2h2l1.5-2h5L16 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
              {phase === 'requesting' ? '準備中…' : 'スタート'}
            </button>
          )}

          <button
            type="button"
            aria-label="カメラを切り替える"
            onClick={flip}
            disabled={inSession || phase === 'requesting'}
            className="absolute right-0 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 backdrop-blur-md disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 0 0-15.5-6.2L3 8" />
              <path d="M3 3v5h5" />
              <path d="M3 12a9 9 0 0 0 15.5 6.2L21 16" />
              <path d="M21 21v-5h-5" />
            </svg>
          </button>
        </div>
      </div>
    </main>
  )
}
